/**
 * Auth store — Zustand, mirrors Supabase auth state into a
 * UI-shaped slice.
 *
 * Backend: Supabase (`@supabase/supabase-js`). Email + password +
 * Google OAuth + email verification + forgot-password. The store
 * does NOT persist credentials itself — Supabase owns the session
 * (in its own localStorage namespace under the project ref). What
 * we persist here is the lightweight `AuthUser` projection so the
 * very first React render has a username to show without waiting
 * for `getSession()` to resolve.
 *
 * Boot flow:
 *
 *   1. App mounts → `bootstrapAuth()` (called once from the root
 *      router or App.tsx) calls `supabase.auth.getSession()` and
 *      hydrates the store. It also subscribes to
 *      `onAuthStateChange` so a token refresh / OAuth redirect /
 *      logout in another tab flips the UI synchronously.
 *
 *   2. The four mock methods (`signupWithEmail`, `loginWithEmail`,
 *      `loginWithGoogle`, `requestPasswordReset`) now call into
 *      `supabase.auth` and let the subscription update the store.
 *      They throw with the same coded errors the auth pages
 *      already handle (`'email_taken'`, `'credentials_invalid'`,
 *      `'email_unverified'`, `'auth_unconfigured'`).
 *
 *   3. `logout()` calls `supabase.auth.signOut()` and clears the
 *      slice synchronously so the UI doesn't flash a stale name.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Session, User } from '@supabase/supabase-js';

import {
  getSupabase,
  isAdminEmail,
  isSupabaseConfigured,
  subscribeAuthChange,
} from '@/api/supabase';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /** "email" or "google" — controls which login flow refreshes the
   *  session. Sourced from Supabase's `app_metadata.provider`. */
  provider: 'email' | 'google';
  /** Mirrors Supabase's `email_confirmed_at` — truthy when the
   *  verification email has been clicked. */
  verified: boolean;
  /** True when the email is in the static admin allow-list. Drives
   *  visibility of `/omega`, `/spark`, `/command`. */
  is_admin: boolean;
  createdAt: string;
}

interface AuthState {
  user: AuthUser | null;
  /** True for the very first login of this user — set on signup
   *  success so the post-login redirect can route to `/onboarding`. */
  needsOnboarding: boolean;
  /** True until `bootstrapAuth()` has resolved at least once.
   *  Auth-gated UI shows a spinner during this window so we don't
   *  flash the unauthenticated layout to a logged-in user. */
  hydrated: boolean;
  signupWithEmail: (input: { name: string; email: string; password: string }) => Promise<void>;
  loginWithEmail: (input: { email: string; password: string }) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  markOnboarded: () => void;
  logout: () => Promise<void>;
}

function projectUser(user: User | null): AuthUser | null {
  if (!user) return null;
  const email = (user.email ?? '').toLowerCase();
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const name =
    (typeof meta.full_name === 'string' ? meta.full_name : undefined) ??
    (typeof meta.name === 'string' ? meta.name : undefined) ??
    email.split('@')[0] ??
    '';
  // Supabase stores the OAuth provider under `app_metadata.provider`.
  // Anything other than `'google'` is treated as the email/password
  // path; future providers (apple, github) widen this case.
  const appMeta = (user.app_metadata ?? {}) as Record<string, unknown>;
  const rawProvider =
    typeof appMeta.provider === 'string' ? appMeta.provider : 'email';
  const provider: AuthUser['provider'] = rawProvider === 'google' ? 'google' : 'email';
  return {
    id: user.id,
    name,
    email,
    provider,
    verified: Boolean(user.email_confirmed_at),
    is_admin: isAdminEmail(email),
    createdAt: user.created_at,
  };
}

function projectSession(session: Session | null): AuthUser | null {
  return projectUser(session?.user ?? null);
}

/** Coded error helper — keeps thrown error messages stable for the
 *  auth pages' switch statements. */
function authError(code: string): Error {
  return new Error(code);
}

/** Maps Supabase auth errors to our stable coded form. */
function mapSupabaseError(message: string | undefined): Error {
  const m = (message ?? '').toLowerCase();
  if (m.includes('user already registered') || m.includes('already exists')) {
    return authError('email_taken');
  }
  if (m.includes('invalid login credentials') || m.includes('invalid email or password')) {
    return authError('credentials_invalid');
  }
  if (m.includes('email not confirmed')) {
    return authError('email_unverified');
  }
  return authError(message ?? 'auth_failed');
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      needsOnboarding: false,
      hydrated: false,

      signupWithEmail: async ({ name, email, password }) => {
        if (!isSupabaseConfigured()) throw authError('auth_unconfigured');
        const sb = getSupabase();
        const { data, error } = await sb.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
          options: {
            data: { full_name: name.trim() },
            // Supabase sends a confirmation email; this URL is where
            // the user lands after clicking it.
            emailRedirectTo: `${window.location.origin}/auth/verify`,
          },
        });
        if (error) throw mapSupabaseError(error.message);
        // First sign-up flow → mark onboarding regardless of whether
        // a session was issued (Supabase issues a session immediately
        // unless email-confirmation is required project-wide).
        set({
          user: projectUser(data.user),
          needsOnboarding: true,
        });
      },

      loginWithEmail: async ({ email, password }) => {
        if (!isSupabaseConfigured()) throw authError('auth_unconfigured');
        const sb = getSupabase();
        const { data, error } = await sb.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });
        if (error) throw mapSupabaseError(error.message);
        set({ user: projectSession(data.session) });
      },

      loginWithGoogle: async () => {
        if (!isSupabaseConfigured()) throw authError('auth_unconfigured');
        const sb = getSupabase();
        const { error } = await sb.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            // Force fresh consent so account-switching works.
            queryParams: { prompt: 'select_account' },
          },
        });
        if (error) throw mapSupabaseError(error.message);
        // Supabase navigates the browser away to Google; the actual
        // session lands on `/auth/callback` and the auth-state
        // subscription wakes up to populate the store.
      },

      requestPasswordReset: async (email) => {
        if (!isSupabaseConfigured()) throw authError('auth_unconfigured');
        const sb = getSupabase();
        const { error } = await sb.auth.resetPasswordForEmail(
          email.trim().toLowerCase(),
          {
            redirectTo: `${window.location.origin}/auth/reset-password`,
          },
        );
        if (error) throw mapSupabaseError(error.message);
      },

      markOnboarded: () => set({ needsOnboarding: false }),

      logout: async () => {
        // Clear local slice synchronously even if the network call
        // hasn't returned yet — UX should never block on logout.
        set({ user: null, needsOnboarding: false });
        if (isSupabaseConfigured()) {
          // Swallow: a network failure during logout shouldn't
          // re-authenticate the user. The local state is already
          // cleared above.
          await getSupabase().auth.signOut().catch(() => {});
        }
      },
    }),
    {
      name: 'gi-engine.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        needsOnboarding: state.needsOnboarding,
      }),
    },
  ),
);

// ─── Bootstrap ──────────────────────────────────────────────────────

let bootstrapped = false;

/** Hydrate the auth store from the active Supabase session and
 *  install a subscription that mirrors auth-state changes. Idempotent
 *  — call once from the root component. */
export async function bootstrapAuth(): Promise<void> {
  if (bootstrapped) return;
  bootstrapped = true;
  if (!isSupabaseConfigured()) {
    useAuthStore.setState({ hydrated: true });
    return;
  }
  const sb = getSupabase();
  const { data } = await sb.auth.getSession();
  useAuthStore.setState({
    user: projectSession(data.session),
    hydrated: true,
  });
  // Live updates — token refresh, OAuth callback, sign-out from
  // another tab, etc.
  subscribeAuthChange((session) => {
    useAuthStore.setState({ user: projectSession(session) });
  });
}
